/**
 * Serviço responsável pelo gerenciamento de camadas no CorelDraw
 * @module LayerService
 */

import { corelCommunication } from '../communication';
import { sendToTerminal } from '../../terminalLogger';
import { WebViewResponse } from '../types/messages';

/**
 * Opções para distribuição por página
 */
export interface DistribuirPorPaginaOptions {
  scope?: 'all' | 'selection';
  overwrite?: boolean;
  renameOnConflict?: boolean;
  mode?: 'first-run' | 'repair' | 'validate' | 'auto';
  dryRun?: boolean;
  createMissingPages?: boolean;
  canonicalizeNames?: 'never' | 'ifNotCanonical' | 'always';
  adjustPageSize?: 'none' | 'ifMismatch';
  toleranceMm?: number;
  allowRepairMutations?: boolean;
}

export class LayerService {
  private static instance: LayerService;

  private constructor() {}

  /**
   * Obtém a instância única do serviço
   */
  public static getInstance(): LayerService {
    if (!this.instance) {
      this.instance = new LayerService();
    }
    return this.instance;
  }

  /**
   * Organiza objetos em uma camada no CorelDraw
   * @param callback Função opcional para tratar a resposta
   * @returns ID da requisição ou null se falhar
   */
  public organizarCamada(callback?: (response: WebViewResponse) => void): string | null {
    // Passa callback direto no sendActionGuarded (com validação HMAC)
    return corelCommunication.sendActionGuarded(
      'organizar-camada', 
      { scope: 'all', pageScope: 'document' },
      callback
    );
  }

  /**
   * Lista as camadas disponíveis no documento
   * @param callback Função opcional para tratar a resposta
   * @returns ID da requisição ou null se falhar
   */
  public listarCamadas(callback?: (response: WebViewResponse) => void): string | null {
    // Usa sendActionGuarded para validação automática de token/HMAC
    return corelCommunication.sendActionGuarded('listar-camadas', {}, callback);
  }

  /**
   * Executa organizar-camada e aguarda a resposta, retornando objetos normalizados mínimos
   * necessários para a distribuição por página (StaticID e Guid).
   * 
   * Este método centraliza a lógica de organização de camadas com configuração padrão
   * para o pipeline de preparação de pedidos.
   * 
   * @returns Promise que resolve com objetos normalizados ou erro
   */
  public static async organizarCamadaAwait(): Promise<{
    ok: boolean;
    objetos: Array<{ StaticID: string; Guid: string }>;
  }> {
    return new Promise((resolve) => {
      try {
        const payload = {
          scope: 'all',
          prefixoGrupo: 'OBJ_G_',
          prefixoIndividual: 'OBJ_I_',
          resetarExportIndex: true,
          iniciarEm: 1,
          gerarGuid: true,
          preservarNomesExistentes: false,
          responseMode: 'minimal'
        } as any;

        const requestId = ((corelCommunication as any).sendMessageWithIdempotency
          ? (corelCommunication as any).sendMessageWithIdempotency('organizar-camada', payload, 'layer|current', (response: any) => {
            try {
              const isOk = response?.status === 'ok' && (response as any)?.result?.sucesso === true;
              if (isOk) {
                const arr = Array.isArray((response as any)?.result?.objetos) ? (response as any).result.objetos : [];
                const mapped = arr
                  .map((o: any) => ({
                    StaticID: o?.StaticID ?? o?.staticId ?? String(o?.StaticID || ''),
                    Guid: o?.Guid ?? o?.guid ?? String(o?.Guid || ''),
                  }))
                  .filter((x: any) => typeof x.StaticID === 'string' && x.StaticID.length > 0 && typeof x.Guid === 'string' && x.Guid.length > 0);
                resolve({ ok: true, objetos: mapped });
              } else {
                sendToTerminal('❌ [ORGANIZAR] Erro ao organizar camada', { 
                  status: response?.status, 
                  sucesso: (response as any)?.result?.sucesso, 
                  mensagem: (response as any)?.result?.mensagem || (response as any)?.message 
                });
                resolve({ ok: false, objetos: [] });
              }
            } catch {
              sendToTerminal('❌ [ORGANIZAR] Exceção ao processar resposta', {});
              resolve({ ok: false, objetos: [] });
            }
          })
          : corelCommunication.sendActionGuarded('organizar-camada', { ...payload, idempotencyKey: 'layer|current' } as any, (response) => {
            try {
              const isOk = response?.status === 'ok' && (response as any)?.result?.sucesso === true;
              if (isOk) {
                const arr = Array.isArray((response as any)?.result?.objetos) ? (response as any).result.objetos : [];
                const mapped = arr
                  .map((o: any) => ({
                    StaticID: o?.StaticID ?? o?.staticId ?? String(o?.StaticID || ''),
                    Guid: o?.Guid ?? o?.guid ?? String(o?.Guid || ''),
                  }))
                  .filter((x: any) => typeof x.StaticID === 'string' && x.StaticID.length > 0 && typeof x.Guid === 'string' && x.Guid.length > 0);
                resolve({ ok: true, objetos: mapped });
              } else {
                sendToTerminal('❌ [ORGANIZAR] Erro ao organizar camada', { 
                  status: response?.status, 
                  sucesso: (response as any)?.result?.sucesso, 
                  mensagem: (response as any)?.result?.mensagem || (response as any)?.message 
                });
                resolve({ ok: false, objetos: [] });
              }
            } catch {
              sendToTerminal('❌ [ORGANIZAR] Exceção ao processar resposta', {});
              resolve({ ok: false, objetos: [] });
            }
          }));

        if (!requestId) {
          sendToTerminal('❌ [ORGANIZAR] Falha ao enviar requisição', {});
          resolve({ ok: false, objetos: [] });
        }
      } catch (error) {
        sendToTerminal('❌ [ORGANIZAR] Exceção ao preparar requisição', {
          error: error instanceof Error ? error.message : String(error)
        });
        resolve({ ok: false, objetos: [] });
      }
    });
  }

  /**
   * Distribui cada objeto em uma nova página
   * Requer que organizar-camada tenha sido executado imediatamente antes
   * 
   * @param options Opções de configuração para distribuição
   * @returns Promise que resolve com true em caso de sucesso
   */
  public static async distribuirPorPagina(options?: DistribuirPorPaginaOptions): Promise<boolean> {
    const scope = options?.scope ?? 'all';
    const mode = options?.mode ?? 'auto';
    
    return new Promise((resolve) => {
      try {
        // Enviar somente: scope, mode, renameOnConflict (default true)
        const data: any = { 
          scope, 
          mode, 
          renameOnConflict: options?.renameOnConflict ?? true 
        };
        
        const startedAt = Date.now();
        const requestId = corelCommunication.sendActionGuarded('distribuir-por-pagina', data, (response) => {
          try {
            const status = (response as any)?.status;
            const result = (response as any)?.result || {};
            const message = (response as any)?.message || result?.mensagem || '';
            const code = (response as any)?.code || result?.code || '';
            const ok = status === 'ok' && result?.sucesso === true;
            
            // Graceful no-op: documento já distribuído ou nada a corrigir → tratar como sucesso idempotente
            const noop = status === 'erro' && (
              code === 'already_distributed' ||
              code === 'nothing_to_repair' ||
              (typeof message === 'string' && (
                message.toLowerCase().includes('parece distribuído') ||
                message.toLowerCase().includes('nada a corrigir')
              ))
            );
            
            if (!ok && !noop) {
              sendToTerminal('❌ [DISTRIBUIR] Erro ao distribuir por página', {
                status: response?.status,
                message: (response as any)?.message,
                code
              });
            }
            
            resolve(ok || noop);
          } catch {
            sendToTerminal('❌ [DISTRIBUIR] Exceção ao processar resposta', {});
            resolve(false);
          }
        });
        
        if (!requestId) resolve(false);
      } catch {
        resolve(false);
      }
    });
  }
}

/**
 * Instância singleton do LayerService para uso direto.
 */
export const layerService = LayerService.getInstance();

/**
 * Função auxiliar para distribuir por página (compatibilidade)
 * @deprecated Use LayerService.distribuirPorPagina() diretamente
 */
export const distribuirPorPagina = LayerService.distribuirPorPagina;